import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { FullPageSpinner } from '../../common/Spinner/FullPageSpinner';
import { useSelector } from 'react-redux';
import { getEncounterRecord, editEncounter, getActivePractitioners, getQueueRecord } from '../../apps/api/Encounter';
import { editQueueItem } from "../api/WaitingRoom";
import { failed } from '../../common/Toastify';
import { syncMissingParticipantsFromStatusHistory } from '../../../utils/EncounterSupport';
import { axios } from "../../../lib/axios";
import ConfirmMsg from '../../common/components/ConfirmMsg';

const DEFAULT_TARGET = '/app/interaction';

/**
 * EncounterRedirect: loads encounter by query param, updates status, navigates to phone call.
 * Requires: /app/encounter-redirect?encounterId=...
 *
 * Important: getEncounterRecord expects an object `{ encounterId }`, not a bare string.
 */
export default function EncounterRedirect() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const encounterId = searchParams.get('encounterId');
    const user = useSelector((state) => state?.auth?.user);
    const pipelineStartedRef = useRef(false);
    const [encounterIds, setEncounterIds] = useState([]);
    const [confirmMsg, setConfirmMsg] = useState("");
    const [confirmMsgOpen, setConfirmMsgOpen] = useState(false);
    const practitionerName = user?.name?.[0]?.text?.trim();
    /** FHIR Practitioner id for references (matches ViewEncounterModal) */
    const practitionerId =
        user?.['custom:practitioner_id'] || user?.['custom:unique_id'];
    /** PhoneCallCreate / inbound flows often expect this for state */
    const practitionerIdForState = user?.['custom:unique_id'];

    const properRedirect = (target, encounter = null) => {
        navigate(target, {
            replace: true,
            state: encounter ? {
                practitionerName,
                practitionerId: practitionerId,
                practitionerUniqueId: practitionerIdForState,
                patientId: encounter?.subject?.reference?.split('/')?.[1],
                patientName: encounter?.subject?.display,
                encounterId,
                currentUser: {
                    patient: { display: encounter?.subject?.display },
                },
                proceedWith: 'WAITING_ROOM',
                requestedPeriod: encounter?.requestedPeriod,
                intakeQuestions: encounter?.intakeQuestions,
                period: encounter?.period,
                callInitiatedFrom: 'inboundCall',
            } : null
        });
    }
    useEffect(() => {
        const fetchData = async () => {
            try { 
                const utcString = new Date().toISOString().split(".")[0] + ".00Z";
                if (!encounterId) {
                    failed('Encounter ID is required');
                    properRedirect('/app/dashboard');
                    return;
                }
                if (!practitionerId || !practitionerName) {
                    failed('Practitioner is required');
                    properRedirect('/app/dashboard');
                    return;
                }
                if (pipelineStartedRef.current) {
                    return;
                }
                pipelineStartedRef.current = true;
                
                let encounterRes = await getEncounterRecord({ encounterId });
                let encounter = encounterRes?.data || null;
                if(!encounter) {
                    failed(
                        encounterRes?.response?.data?.message ||
                        encounterRes?.response?.data?.error ||
                        encounterRes?.message ||
                        'Failed to get encounter'
                    );
                    properRedirect('/app/dashboard');
                    return; 
                }
                // check if there are any active encounters for the current practitioner
                let activeEncounters = await axios.get(`encounter/search?activePractitioner=${practitionerId}&status=in-progress-waitingroom&orgId=${encounter?.orgId}`);
                activeEncounters = activeEncounters?.data || [];
                // if the current encounter is not in the active encounters list, then show the confirmation modal
                if(activeEncounters?.length > 0 && !activeEncounters?.some(encounter => encounter?.id == encounterId)) {
                    let multiple = activeEncounters?.length > 1 ? "s" : "";
                    const idList = activeEncounters?.map((encounter) => encounter?.id).join(", ");
                    setConfirmMsg(
                        <>
                        You already have {multiple.length > 0 ? "active" : "an"} encounter{multiple} in progress.
                        Please complete {multiple.length > 0 ? "them" : "it"} before starting a new one.{" "}
                        <br /><strong>{idList}</strong>
                        </>
                    );
                    setEncounterIds(activeEncounters?.map(encounter => encounter?.id));
                    setConfirmMsgOpen(true);
                    return;
                }   

                const activePractitioners = getActivePractitioners(encounter);
                const isActive = getActivePractitioners(encounter, practitionerId).length > 0; // if activePractitioners is 0 length, then the current practitioner is not the active practitioner

                // if already completed
                if(encounter?.status === "finished") {
                    properRedirect(`/app/encounter-details?encounterId=${encounterId}`);
                    return;
                }
                if(encounter?.status !== "arrived" && activePractitioners?.length > 0 && !isActive) {
                    failed("Encounter is already in progress by another practitioner");
                    properRedirect('/app/dashboard');
                    return;
                }

                properRedirect(DEFAULT_TARGET, encounter);
                return;
            }
            catch(e) {
                failed("User does not have access to this encounter");
                properRedirect('/app/dashboard');
                return;
            }
        }
        fetchData();
    }, [
        encounterId,
        practitionerId,
        practitionerName,
        practitionerIdForState,
        navigate,
    ]);

    return (
        <>
            
            <ConfirmMsg
                open={confirmMsgOpen}
                onConfirm={() => {
                    setConfirmMsgOpen(false);
                    properRedirect('/app/dashboard');
                }}
                onDismiss={() => {setConfirmMsgOpen(false); 
                    properRedirect('/app/dashboard');}}
                showCancel={false}
                actionBtnTitle="Ok"
                title={`Encounter${encounterIds.length > 1 ? "s" : ""} in Progress`}
                message={confirmMsg}
            />
            <FullPageSpinner hide={ confirmMsgOpen ? true : false} />
        </>
    );
}
